import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/SimpleAuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rbac-tabpanel-${index}`}
      aria-labelledby={`rbac-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Mock data for roles
const mockRoles = [
  {
    id: 1,
    name: 'Administrator',
    description: 'Full system access',
    permissions: ['all'],
    users: 3,
    isSystem: true
  },
  {
    id: 2,
    name: 'Compliance Officer',
    description: 'Manage compliance frameworks and obligations',
    permissions: ['frameworks.manage', 'obligations.manage', 'controls.manage', 'reports.view'],
    users: 5,
    isSystem: true
  },
  {
    id: 3,
    name: 'Auditor',
    description: 'View and audit compliance activities',
    permissions: ['frameworks.view', 'obligations.view', 'controls.view', 'reports.view', 'audit.manage'],
    users: 2,
    isSystem: true
  },
  {
    id: 4,
    name: 'Department Manager',
    description: 'Manage department tasks and team',
    permissions: ['tasks.manage', 'team.view', 'reports.department'],
    users: 8,
    isSystem: false
  }
];

// Mock data for permissions
const mockPermissions = [
  { category: 'Frameworks', permissions: [
    { key: 'frameworks.view', label: 'View Frameworks' },
    { key: 'frameworks.create', label: 'Create Frameworks' },
    { key: 'frameworks.edit', label: 'Edit Frameworks' },
    { key: 'frameworks.delete', label: 'Delete Frameworks' },
    { key: 'frameworks.manage', label: 'Manage Frameworks (All)' }
  ]},
  { category: 'Obligations', permissions: [
    { key: 'obligations.view', label: 'View Obligations' },
    { key: 'obligations.create', label: 'Create Obligations' },
    { key: 'obligations.edit', label: 'Edit Obligations' },
    { key: 'obligations.delete', label: 'Delete Obligations' },
    { key: 'obligations.manage', label: 'Manage Obligations (All)' }
  ]},
  { category: 'Controls', permissions: [
    { key: 'controls.view', label: 'View Controls' },
    { key: 'controls.create', label: 'Create Controls' },
    { key: 'controls.edit', label: 'Edit Controls' },
    { key: 'controls.delete', label: 'Delete Controls' },
    { key: 'controls.manage', label: 'Manage Controls (All)' }
  ]},
  { category: 'Tasks', permissions: [
    { key: 'tasks.view', label: 'View Tasks' },
    { key: 'tasks.create', label: 'Create Tasks' },
    { key: 'tasks.edit', label: 'Edit Tasks' },
    { key: 'tasks.complete', label: 'Complete Tasks' },
    { key: 'tasks.manage', label: 'Manage Tasks (All)' }
  ]},
  { category: 'Reports', permissions: [
    { key: 'reports.view', label: 'View Reports' },
    { key: 'reports.create', label: 'Create Reports' },
    { key: 'reports.export', label: 'Export Reports' },
    { key: 'reports.department', label: 'Department Reports' }
  ]},
  { category: 'System', permissions: [
    { key: 'users.manage', label: 'Manage Users' },
    { key: 'roles.manage', label: 'Manage Roles' },
    { key: 'settings.manage', label: 'Manage Settings' },
    { key: 'audit.view', label: 'View Audit Trail' },
    { key: 'audit.manage', label: 'Manage Audit Trail' }
  ]}
];

export default function MaterialRBACManagementPage() {
  const [tabValue, setTabValue] = useState(0);
  const [roles, setRoles] = useState(mockRoles);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState<any>(null);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [viewingRolePermissions, setViewingRolePermissions] = useState<any>(null);
  const { user } = useAuth();

  // Form state
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', permissions: [] });
    setRoleDialogOpen(true);
  };

  const handleEditRole = (role: any) => {
    if (role.isSystem) {
      // Show alert that system roles cannot be edited
      alert('System roles cannot be edited');
      return;
    }
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description,
      permissions: role.permissions
    });
    setRoleDialogOpen(true);
  };

  const handleDeleteRole = (role: any) => {
    if (role.isSystem) {
      alert('System roles cannot be deleted');
      return;
    }
    setDeletingRole(role);
    setDeleteDialogOpen(true);
  };

  const handleViewPermissions = (role: any) => {
    setViewingRolePermissions(role);
    setPermissionDialogOpen(true);
  };

  const handleSaveRole = () => {
    if (editingRole) {
      // Update existing role
      setRoles(roles.map(r => 
        r.id === editingRole.id 
          ? { ...r, ...roleForm }
          : r
      ));
    } else {
      // Create new role
      setRoles([...roles, {
        id: Math.max(...roles.map(r => r.id)) + 1,
        ...roleForm,
        users: 0,
        isSystem: false
      }]);
    }
    setRoleDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    setRoles(roles.filter(r => r.id !== deletingRole.id));
    setDeleteDialogOpen(false);
    setDeletingRole(null);
  };

  const handlePermissionChange = (permission: string) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const handleDuplicateRole = (role: any) => {
    setEditingRole(null);
    setRoleForm({
      name: `${role.name} (Copy)`,
      description: role.description,
      permissions: [...role.permissions]
    });
    setRoleDialogOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        RBAC Management
      </Typography>

      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab 
                icon={<GroupIcon />} 
                iconPosition="start" 
                label="Roles" 
              />
              <Tab 
                icon={<AssignmentIcon />} 
                iconPosition="start" 
                label="Permissions" 
              />
              <Tab 
                icon={<SecurityIcon />} 
                iconPosition="start" 
                label="Audit Log" 
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">System Roles</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateRole}
                >
                  Create Role
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Role Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Users</TableCell>
                      <TableCell align="center">Type</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <Typography fontWeight="medium">{role.name}</Typography>
                        </TableCell>
                        <TableCell>{role.description}</TableCell>
                        <TableCell align="center">{role.users}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={role.isSystem ? 'System' : 'Custom'} 
                            size="small" 
                            color={role.isSystem ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Permissions">
                            <IconButton size="small" onClick={() => handleViewPermissions(role)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate Role">
                            <IconButton size="small" onClick={() => handleDuplicateRole(role)}>
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={role.isSystem ? "System roles cannot be edited" : "Edit Role"}>
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditRole(role)}
                                disabled={role.isSystem}
                              >
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={role.isSystem ? "System roles cannot be deleted" : "Delete Role"}>
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteRole(role)}
                                disabled={role.isSystem}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info">
                System roles are predefined and cannot be modified or deleted. You can create custom roles based on your organization's needs.
              </Alert>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <Typography variant="h6">Permission Categories</Typography>
              
              {mockPermissions.map((category) => (
                <Card key={category.category} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      {category.category}
                    </Typography>
                    <Grid container spacing={2}>
                      {category.permissions.map((permission) => (
                        <Grid item xs={12} sm={6} md={4} key={permission.key}>
                          <Box sx={{ 
                            p: 1.5, 
                            border: 1, 
                            borderColor: 'divider', 
                            borderRadius: 1,
                            backgroundColor: 'background.paper'
                          }}>
                            <Typography variant="body2" fontWeight="medium">
                              {permission.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {permission.key}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Stack spacing={3}>
              <Typography variant="h6">RBAC Audit Log</Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>2025-05-28 10:30 AM</TableCell>
                      <TableCell>{user?.name || 'Admin User'}</TableCell>
                      <TableCell>Created Role</TableCell>
                      <TableCell>Created custom role "Project Manager"</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>2025-05-28 09:15 AM</TableCell>
                      <TableCell>{user?.name || 'Admin User'}</TableCell>
                      <TableCell>Modified Permissions</TableCell>
                      <TableCell>Added "reports.export" to Auditor role</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>2025-05-27 04:45 PM</TableCell>
                      <TableCell>{user?.name || 'Admin User'}</TableCell>
                      <TableCell>Assigned Role</TableCell>
                      <TableCell>Assigned "Compliance Officer" to user John Doe</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Role Create/Edit Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Role Name"
              fullWidth
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={roleForm.description}
              onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
            />
            
            <Typography variant="subtitle1" fontWeight="medium">
              Permissions
            </Typography>
            
            {mockPermissions.map((category) => (
              <Card key={category.category} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {category.category}
                  </Typography>
                  <FormGroup>
                    {category.permissions.map((permission) => (
                      <FormControlLabel
                        key={permission.key}
                        control={
                          <Checkbox
                            checked={roleForm.permissions.includes(permission.key)}
                            onChange={() => handlePermissionChange(permission.key)}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{permission.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {permission.key}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveRole} 
            variant="contained"
            disabled={!roleForm.name}
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{deletingRole?.name}"? 
            This action cannot be undone.
          </Typography>
          {deletingRole?.users > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This role is currently assigned to {deletingRole.users} user(s). 
              They will need to be reassigned to another role.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Permissions Dialog */}
      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Permissions for {viewingRolePermissions?.name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {viewingRolePermissions?.permissions.includes('all') ? (
              <Alert severity="info">
                This role has full system access (all permissions).
              </Alert>
            ) : (
              viewingRolePermissions?.permissions.map((perm: string) => (
                <Chip key={perm} label={perm} size="small" />
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
